import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import {
  IconCrown,
  IconDice3,
  IconHelpHexagon,
  IconMenu2,
  IconMoon,
  IconPremiumRights,
  IconSun,
  type Icon,
} from "@tabler/icons-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { openLoginDialog } from "@/stores/loginDialog";
import { themeMode, useThemeMode } from "@/stores/theme_mode";
import { useUserStore } from "@/stores/user";

export interface NavigationItem {
  label: string;
  to: string;
  icon?: Icon;
  activePath?: string;
}

const defaultItems: NavigationItem[] = [
  { label: "Kincir", to: "/kincir", icon: IconCrown },
  { label: "Dadu", to: "/dadu", icon: IconDice3 },
  { label: "Paket", to: "/pricing", icon: IconPremiumRights },
  { label: "About", to: "/about", icon: IconHelpHexagon },
];

interface NavigationProps {
  items?: NavigationItem[];
  logoSrc?: string;
  logoAlt?: string;
  homeTo?: string;
  profileTo?: string;
}

function Navigation({
  items = defaultItems,
  logoSrc = "/logo-64.webp",
  logoAlt = "Batu Ice Logo",
  homeTo = "/",
  profileTo = "/profile",
}: NavigationProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme] = useThemeMode();
  const info = useUserStore((state) => state.info);
  const packages = useUserStore((state) => state.packages);
  const logout = useUserStore((state) => state.logout);
  const profileInitials = info?.name.trim().slice(0, 3) || "Hi";
  const packageName = useMemo(
    () => Object.keys(packages ?? {}).find((key) => key !== "free" && key !== "registered") ?? "Free",
    [packages],
  );
  const isDark = theme === "dark";
  const themeLabel = isDark ? "Enable light mode" : "Enable dark mode";

  useEffect(() => setMobileMenuOpen(false), [location.pathname, location.search]);

  const switchTheme = () => themeMode.switchMode();
  const isItemActive = (item: NavigationItem) => {
    const activePath = item.activePath ?? item.to;
    return location.pathname === activePath || location.pathname.startsWith(`${activePath}/`);
  };

  return (
    <header className="print:hidden">
      <div className="bg-card py-1 shadow-secondary shadow-[inset_0_-1px_0]">
        <div className="app-container relative flex h-12 items-center">
          <button type="button" className="mr-3 inline-flex size-8 items-center justify-center rounded-sm border-0 text-foreground transition-colors hover:bg-muted md:hidden" aria-expanded={mobileMenuOpen} aria-controls="navbar-menu" aria-label="Toggle navigation" onClick={() => setMobileMenuOpen((open) => !open)}>
            <IconMenu2 className="size-5" aria-hidden="true" />
          </button>

          <Link className="absolute left-1/2 inline-flex -translate-x-1/2 items-center md:static md:mr-6 md:translate-x-0" to={homeTo}>
            <img alt={logoAlt} className="h-8 w-8" height={32} src={logoSrc} width={32} />
          </Link>

          <div className="ml-auto flex items-center gap-4">
            <button type="button" className="hidden size-8 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:inline-flex" title={themeLabel} aria-label={themeLabel} onClick={switchTheme}>
              {isDark ? <IconSun className="size-5" aria-hidden="true" /> : <IconMoon className="size-5" aria-hidden="true" />}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="flex items-center rounded-sm text-left leading-none text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Profile menu">
                  <span className="inline-flex size-10 items-center justify-center rounded-xl bg-background text-base font-medium uppercase text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)]">{profileInitials}</span>
                  <span className="ml-2 hidden md:block"><span className="block text-sm leading-5">{info?.name ?? "Guest"}</span><span className="mt-0.5 block text-xs leading-4 text-muted-foreground">{packageName}</span></span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {info ? <>
                  <DropdownMenuItem asChild><NavLink to={profileTo}>Profile</NavLink></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="button" onSelect={logout}>Logout</DropdownMenuItem>
                </> : <DropdownMenuItem onSelect={openLoginDialog}>Login</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div id="navbar-menu" className={`${mobileMenuOpen ? "block" : "hidden"} bg-card py-1 shadow-secondary shadow-[inset_0_-1px_0] md:block`}>
        <nav className="app-container" aria-label="Main navigation">
          <ul className="flex flex-col md:h-12 md:flex-row md:items-center">
            {items.map((item) => {
              const ItemIcon = item.icon;
              const active = isItemActive(item);
              return <li key={item.to}><NavLink className={`relative flex h-10 items-center gap-1 rounded-xl px-3 text-sm transition-colors hover:bg-muted hover:text-foreground after:absolute after:inset-x-0 after:-bottom-2 after:hidden after:h-0.5 md:after:block ${active ? "text-foreground after:bg-primary" : "text-muted-foreground after:bg-transparent"}`} to={item.to}>
                {ItemIcon && <ItemIcon className="size-5" aria-hidden="true" />}
                <span>{item.label}</span>
              </NavLink></li>;
            })}
          </ul>
        </nav>
      </div>
    </header>
  );
}

export { Navigation };
export type { NavigationProps };
